import React, {
  createContext,
  KeyboardEventHandler,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import styles from '../Tabs.module.scss';
import ComposableTabsContext from './ComposableTabsContext';

type ComposableTabsListProps = {
  /** Add (translated) aria-label to TabList to improve accessibility */
  'aria-label'?: string;
  className?: string;
};

export const ComposableTabsListContext = createContext({
  activatedTab: '',
});

const reactTo = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Home', 'End'];

/** ComposableTabsList is parent component of ComposableTabsTab that handles keyboard navigation context for tab buttons
 * It should be rendered inside ComposableTabs component above ComposableTabsPanel
 * and it should only contain ComposableTabsTab's as children */
const ComposableTabsList: React.FunctionComponent<ComposableTabsListProps> = ({
  className,
  children,
}) => {
  const { tabsId } = useContext(ComposableTabsContext);
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({
    activatedTab: '',
  });
  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!reactTo.includes(e.key)) {
        return;
      }
      const element = ref.current as HTMLDivElement;
      const tabs = [
        ...element.querySelectorAll(`[data-tab-for="${tabsId}"]`),
      ] as HTMLButtonElement[];
      const index = tabs.indexOf(e.target as HTMLButtonElement);

      let targetIndex: number;

      if (e.key === 'ArrowRight') {
        // next sibling or if this is last then first sibling
        targetIndex = (index + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft') {
        // previous sibling or if this is first then last sibling
        targetIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        // first sibling
        targetIndex = 0;
      } else {
        // 'End' last sibling
        targetIndex = tabs.length - 1;
      }
      const target = tabs[targetIndex];
      const { tabKey } = target?.dataset;
      setState({ activatedTab: tabKey as string });
    },
    [tabsId, setState]
  );
  return (
    <div
      id={`${tabsId}-tab-list`}
      className={classNames(styles.tabs_tabsContainer, className)}
      ref={ref}
      onKeyDown={onKeyDown}
    >
      <ComposableTabsListContext.Provider value={state}>
        {children}
      </ComposableTabsListContext.Provider>
    </div>
  );
};

export default ComposableTabsList;
